import { Injectable, inject } from '@angular/core';
import {
    WORKFLOW_MAP,
    WorkflowType,
    WorkflowOrigin,
    WorkflowStep,
    WorkflowStepType,
    StorageConstants,
} from '@warema/shared/models';
import { TenantConfigurationService } from '@warema/shared/tenant-config';

@Injectable({
    providedIn: 'root',
})
export class WorkflowStepsService {
    private readonly _tenantConfig = inject(TenantConfigurationService);

    public getSteps(
        workflowType: WorkflowType,
        origin: WorkflowOrigin,
        jumpedFromCompanySearch = false,
    ): Array<WorkflowStep> {
        let steps = WORKFLOW_MAP.get(workflowType)?.get(origin) ?? [];

        if (jumpedFromCompanySearch || origin === WorkflowOrigin.RegionalSea) {
            steps = this._validatePreviousSteps(steps);
        }

        if (origin === WorkflowOrigin.BusinessCard) {
            steps = this._validatePreviousSteps(steps, WorkflowStepType.Dates);
        }

        return this._filterStepsByLCCConfig(steps);
    }

    public getInitialStepTypeForWorkflow(
        steps: Array<WorkflowStep>,
        workflowOrigin: WorkflowOrigin,
        workflowType: WorkflowType,
        desiredInitialStep?: WorkflowStepType,
        jumpedFromCompanySearch = false,
    ): WorkflowStepType | null {
        localStorage.removeItem(StorageConstants.JUMPED_FROM_COMPANY_SEARCH);

        if (desiredInitialStep && !steps.some(x => x.workflowStepType === desiredInitialStep)) {
            console.error(`There is no such step for this workflow`);
            return null;
        }
        if (workflowOrigin === WorkflowOrigin.BusinessCard) {
            return WorkflowStepType.Dates;
        }

        if (workflowOrigin === WorkflowOrigin.RegionalSea && workflowType === WorkflowType.CompanySearch) {
            return WorkflowStepType.Search;
        }
        if (desiredInitialStep) {
            if (
                [
                    WorkflowStepType.ProductCategories,
                    WorkflowStepType.Activity,
                    WorkflowStepType.Search,
                    WorkflowStepType.Dates,
                ].includes(desiredInitialStep)
            ) {
                return desiredInitialStep;
            } else if (
                [WorkflowStepType.AppointmentDetails, WorkflowStepType.ContactInformation].includes(
                    desiredInitialStep,
                ) &&
                workflowType === WorkflowType.Appointment
            ) {
                return WorkflowStepType.Dates;
            } else if (workflowType === WorkflowType.Offer) {
                return WorkflowStepType.Search;
            }
        }
        if (!jumpedFromCompanySearch) {
            return null;
        }

        return WorkflowStepType.Dates;
    }

    private _filterStepsByLCCConfig(steps: Array<WorkflowStep>): Array<WorkflowStep> {
        if (this._tenantConfig.settings && !this._tenantConfig.settings.companySearch.showActivities) {
            steps = steps?.filter(x => x.workflowStepType !== WorkflowStepType.Activity);
        }
        return steps;
    }

    private _validatePreviousSteps(
        steps: Array<WorkflowStep>,
        initialStepType: WorkflowStepType = WorkflowStepType.Search,
    ): Array<WorkflowStep> {
        const currentStepPosition = steps.findIndex(x => x.workflowStepType === initialStepType);

        return steps.map((step, stepPosition) => {
            if (stepPosition <= currentStepPosition) {
                step = { ...step, isValid: true };
            }
            return step;
        });
    }
}
